/**
 * Project setup redesign — Track D (Connections page + repository/support/chat wizards): namespaces `projectConnections`, `projectSetupRepo`, `projectSetupSupport`, `projectSetupChat`.
 *
 * Top-level namespaces owned by this track only. `connectionsSetupNl` is typed against
 * `connectionsSetupEn`, so a key missing in Dutch fails typecheck exactly as it does in
 * the main dictionary.
 *
 * Reason codes (`SetupReasonCode`) are NOT repeated here: every surface reads
 * the setup backend's `oauthReturn.reason.<code>` so one failure is described
 * the same way everywhere.
 */
export const connectionsSetupEn = {
  projectConnections: {
    title: "Connections",
    subtitle: "Which systems this project uses, whether they work, and adding one.",
    scopeNote:
      "Connections belong to your organization and can be shared by several projects. Here you choose which of them this project uses, and what for. Removing one from this project never disconnects it for the rest of the company.",
    readOnly: "You can see this project's connections. To add or change one, ask an administrator of this project or of your organization.",
    /** Names who can do what the viewer cannot (admins/managers of the organization). */
    orgAdmins: "Administrators of your organization: {names}.",
    canUseNotCreate:
      "You can connect this project to connections your organization already has. Adding a new company connection needs an organization administrator.",
    askAdmin: "Ask a project administrator",
    loadError: "The connections of this project could not be loaded. Reload the page to try again.",
    groups: {
      email: {
        title: "Email",
        description: "Mailboxes whose mail becomes work in this project.",
        add: "Connect mailbox",
        empty: "No mailbox is connected to this project yet.",
        emptyAction: "Connect mailbox",
      },
      chat: {
        title: "Chat",
        description: "Slack or Microsoft Teams channels for new requests, progress updates and approvals.",
        add: "Add chat channel",
        empty: "No chat channel is connected to this project yet.",
        emptyAction: "Add chat channel",
      },
      support: {
        title: "Support software",
        description: "Your helpdesk or issue tracker: Freshdesk, TOPdesk, Odoo, Jira or Linear.",
        add: "Connect support software",
        empty: "No support software is connected to this project yet.",
        emptyAction: "Connect support software",
      },
      code: {
        title: "Code",
        description: "The repositories FixControl reads and proposes changes for.",
        add: "Connect repository",
        empty: "No repository is connected to this project yet.",
        emptyAction: "Connect repository",
      },
      deployments: {
        title: "Deployments and alerts",
        technical: "For technical teams",
        show: "Deployments and alerts (for technical teams)",
        description: "Kubernetes clusters, Argo rollouts and system alerts that belong to this project.",
        add: "Add deployment connection",
        empty: "No deployment connection yet. Most projects do not need one.",
        emptyAction: "Add deployment connection",
      },
    },
    status: {
      working: "Working",
      untested: "Not tested yet",
      attention: "Needs attention",
      reconnect: "Reconnect required",
      disabled: "Disabled",
    },
    statusHint: {
      working: "Everything checked out.",
      untested: "Set up, but nothing has proved yet that it works. Test the connection to be sure.",
      attention: "Something is wrong with this connection. Test it to see what.",
      reconnect: "The sign-in with {provider} has expired or was revoked. Sign in again to restore it.",
      disabled: "Paused for this project. Nothing is received or sent through it.",
      noCompanyConnection: "Nobody has connected {provider} for your organization yet, so this cannot work.",
      deliveryFailing: "Recent messages to this connection could not be delivered.",
    },
    card: {
      unnamed: "Unnamed {provider} connection",
      usedFor: "Used by this project for: {purposes}",
      companyConnection: "Company connection: {account}",
      companyConnectionGeneric: "Company connection: your organization's {provider}",
      lastChecked: "Last checked",
      neverChecked: "Not checked yet",
      test: "Test connection",
      testing: "Testing…",
      manage: "Manage",
      remove: "Remove from this project",
      reconnect: "Reconnect",
      fixConnection: "Fix the connection",
      defaultRepository: "Default repository",
      codeSourceOther: "Server or folder",
    },
    purpose: {
      intake: "New requests become work here",
      approvals: "Approval requests",
      notifications: "Progress updates",
      status: "Status back to the source",
      delivery: "Proposed changes (pull requests)",
      deployment: "Deployments",
      incidents: "Alerts become work here",
      code: "Code FixControl works on",
    },
    purposeSupport: {
      intake: "Linked tickets belong to this project",
      notifications: "Post progress back to the ticket",
      status: "Ticket status follows the work",
    },
    purposeChat: {
      intake: "New requests from this channel",
      notifications: "Progress updates",
      approvals: "Approval requests",
    },
    checkMessage: {
      provider_health_pass: "The connection with {provider} works.",
      provider_health_unknown: "{provider} has not been checked recently. The result is not certain yet.",
      provider_health_degraded: "{provider} works, but not everything went smoothly recently.",
      provider_health_disabled: "This connection is switched off.",
      provider_health_no_connection_needed: "No sign-in is needed for this connection.",
      test_message_pass: "The test message was posted.",
      test_message_queued: "The test message is queued and will be posted shortly.",
      test_message_confirmation_required: "A test message was not sent, because it needs your confirmation.",
      test_message_organization_only: "No test message was sent. Only an organization administrator can post one in a channel.",
    },
    checkStatus: {
      pass: "Passed.",
      fail: "Did not pass.",
      warn: "Passed with a warning.",
      skipped: "Not checked.",
    },
    test: {
      confirmTitle: "Send a test message to {name}?",
      confirmBody:
        "FixControl posts one short, visible test message in {name}. Everyone in the channel can see it. Nothing else changes.",
      confirmAction: "Send test message",
    },
    remove: {
      title: "Remove {name} from this project?",
      body:
        "This project stops using {name}. The company connection to {provider} stays, and other projects that use it are not affected. You can add it again later.",
      bodyCodebase:
        "This project stops working on {name}. The repository stays connected to your organization, and other projects that use it are not affected.",
      confirm: "Remove from this project",
      failed: "It could not be removed. Try again, or ask a project administrator.",
    },
    manage: {
      loadFailedTitle: "Could not open this connection",
      loadFailed: "The details of this connection could not be loaded.",
    },
    twoFactor: {
      setup: "For your security, set up two-step verification for your account first. Then try again.",
      reauth: "Sign in again to confirm your two-step verification, then try again.",
    },
    globalPages: {
      infraGitHint: "GitHub, GitLab and Bitbucket are not connected here, but under Channels → Code & patch delivery.",
      infraGitLink: "Connect GitHub, GitLab or Bitbucket",
      codebasesGitCta: "Connect GitHub, GitLab or Bitbucket",
      codebasesServerCta: "Is your code on your own server or in a local folder? Connect that here instead.",
    },
    wizard: {
      stepOf: "Step {step} of {total}",
      finished: "Finished",
      dialogLabel: "{wizard} for {project}",
      back: "Back",
      next: "Next",
      cancel: "Cancel",
      close: "Close",
      done: "Done",
      tryAgain: "Try again",
      loading: "Loading…",
      working: "Working…",
      technicalDetails: "Technical details",
    },
  },

  projectSetupRepo: {
    title: "Connect a repository",
    noPermission: "Only an administrator of your organization can connect a repository to this project.",
    onlyExisting:
      "You can link a repository your organization already has. Only an administrator of your organization can add a new one.",
    genericProvider: "the code host",
    linkButton: "Connect to {project}",
    retryLink: "Try linking again",
    partial:
      "The repository was added to your organization, but linking it to {project} did not work. Try again; nothing is added twice.",
    steps: {
      provider: "Where is the code?",
      connect: "Sign in",
      organization: "Choose the organization",
      repository: "Choose the repository",
      folder: "Choose the folder",
      branch: "Choose the branch",
      check: "Check",
      done: "Done",
    },
    sources: {
      github: { name: "GitHub", hint: "" },
      gitlab: { name: "GitLab", hint: "" },
      bitbucket: { name: "Bitbucket", hint: "" },
      local: { name: "Folder on a computer", hint: "A folder on a computer that runs the FixControl desktop app." },
      ssh: { name: "Server (SSH)", hint: "A folder on one of your own servers, reached over SSH." },
    },
    provider: {
      question: "Where is the code?",
      intro: "Choose where the code of this project lives. FixControl reads it and proposes changes as pull requests; it never changes your main branch directly.",
      more: "More options (technical)",
      moreHint: "Only when the code is not on GitHub, GitLab or Bitbucket.",
    },
    existing: {
      title: "Already connected in your organization",
      hint: "Another project already uses these. Choose one to use it here too.",
    },
    connect: {
      intro: "FixControl reaches {provider} through a connection that belongs to your organization. This project then uses one repository from it.",
      introServer: "FixControl reaches this code through a connection that belongs to your organization.",
      useExisting: "Use a connection your organization already has",
      companyConnection: "Company connection: {account}",
      none: "Your organization has no {provider} connection yet.",
      noneVisible: "No {provider} connection is visible to you.",
      newTitle: "Connect a new {provider} account",
      explain:
        "You sign in with {provider} and choose which repositories FixControl may read. You come back here afterwards. The connection belongs to your organization; you can remove it later.",
      signIn: "Continue to {provider}",
      returned: "{provider} is connected. Choose the repository.",
      onlyAdmin: "Only an administrator of your organization can connect a new {provider} account. Ask them, or use an existing connection.",
      notConfigured: "Signing in with {provider} is not set up on this FixControl installation. Ask your FixControl administrator.",
      useToken: "Connect with an access token instead (company page)",
      addServer: "Add a server or folder connection (company page)",
    },
    organization: {
      intro: "This {provider} connection can see several organizations. Which one holds the code?",
      count: "{n} repositories",
    },
    repository: {
      loading: "Loading repositories…",
      search: "Search repositories",
      empty: "This {provider} connection cannot see any repositories yet. Give FixControl access to the repository in {provider}, then come back.",
      noMatches: "No repository matches your search.",
      pickConnectionFirst: "Choose a connection first.",
      manualLabel: "Repository",
      manualHint: "Type it as organization/name, the way it appears in {provider}.",
      localLabel: "Folder",
      sshLabel: "Folder on the server",
      pathHint: "The full path of the folder that holds the code.",
    },
    branch: {
      label: "Branch",
      hint: "FixControl proposes changes against this branch of {repo}. It never pushes to it directly: changes arrive as a pull request for your team to review.",
    },
    check: {
      linked: "{repo} is linked to {project}.",
      read: {
        running: "Checking that FixControl can read the repository…",
        ok: "FixControl can read the repository.",
        fail: "FixControl could not read the repository. Check that the connection has access to it.",
        skipped: "Reading the repository was not checked. An administrator can test it on the Connections page.",
      },
      sandbox: {
        ready: "Test environment: FixControl can try proposed changes in a safe copy.",
        notYet: "Test environment: not checked yet. You can see and test it on the Environments page.",
      },
      preview: {
        ready: "Preview: available. You can open a proposed change in your browser before approving it.",
        notYet: "Preview: becomes available once FixControl has verified that the application starts.",
      },
      openEnvironments: "Open Environments",
    },
    done: {
      title: "{repo} is now connected to {project}.",
      body: "FixControl reads this code when it works on this project's issues and proposes changes as pull requests.",
    },
  },

  projectSetupSupport: {
    title: "Connect support software",
    noPermission: "Only an administrator of this project or of your organization can connect support software to this project.",
    steps: {
      system: "Which system?",
      connect: "Connect",
      test: "Test",
      pick: "Which tickets?",
      purposes: "What should happen?",
      done: "Done",
    },
    systems: {
      freshdesk: { name: "Freshdesk", hint: "Helpdesk tickets" },
      topdesk: { name: "TOPdesk", hint: "Service desk incidents" },
      odoo: { name: "Odoo Helpdesk", hint: "Helpdesk tickets in Odoo" },
      jira: { name: "Jira", hint: "Issues in Jira projects" },
      linear: { name: "Linear", hint: "Issues in Linear teams" },
    },
    system: {
      intro: "Which system do your customers or colleagues report problems in? FixControl turns tickets from it into work in this project.",
    },
    connect: {
      intro: "FixControl reaches {system} through a connection that belongs to your organization. This project then uses part of it.",
      useExisting: "Use the connection your organization already has",
      new: "Connect a new {system} account",
      newHint: "Once, for the whole organization. Other projects can use it too.",
      onlyAdmin: "Your organization has no {system} connection yet, and only an administrator of your organization can add one.",
      formIntro: "Fill in these details from {system}. They are stored encrypted and are never shown again.",
      companyWide: "This connects {system} for your whole organization. You choose what this project uses in the next steps.",
      save: "Connect {system}",
      oauthExplain: "You sign in with {system} and allow FixControl to read and update issues. You come back here afterwards.",
      signIn: "Continue to {system}",
      notConfigured: "Signing in with {system} is not set up on this FixControl installation.",
      useToken: "Connect with an API token instead (company page)",
    },
    fields: {
      freshdesk: {
        baseUrl: { label: "Your Freshdesk address", placeholder: "https://acme.freshdesk.com", help: "The address you open Freshdesk on, for example acme.freshdesk.com." },
        token: { label: "Freshdesk API key", placeholder: "Paste the API key", help: "In Freshdesk: your profile picture, then Profile settings. The API key is on the right." },
      },
      topdesk: {
        baseUrl: { label: "Your TOPdesk address", placeholder: "https://acme.topdesk.net", help: "The address you open TOPdesk on, for example acme.topdesk.net." },
        username: { label: "Operator login name", placeholder: "fixcontrol", help: "The login name of the TOPdesk operator FixControl acts as." },
        token: { label: "Application password", placeholder: "Paste the application password", help: "In TOPdesk: the operator's card, then Application passwords. Not the normal password." },
      },
      odoo: {
        baseUrl: { label: "Your Odoo address", placeholder: "https://acme.odoo.com", help: "The address you open Odoo on, for example acme.odoo.com." },
        host: { label: "Database name", placeholder: "acme", help: "Often the first part of your Odoo address." },
        username: { label: "Login email", placeholder: "you@acme.com", help: "The email address of the Odoo user FixControl acts as." },
        token: { label: "API key", placeholder: "Paste the API key", help: "In Odoo: Preferences, then Account Security, then New API Key." },
      },
    },
    test: {
      created: "{system} is connected for your organization.",
      running: "Checking the connection with {system}…",
      ok: "FixControl can reach {system}.",
      skipped: "The connection was not tested, because only an administrator can. It is used as it is.",
      continueAnyway: "You can continue and fix this later; the project then shows this connection as needing attention.",
    },
    pick: {
      question: {
        freshdesk: "Which tickets belong to this project?",
        topdesk: "Which operator group's incidents belong to this project?",
        odoo: "Which helpdesk team's tickets belong to this project?",
        jira: "Which Jira project belongs to this project?",
        linear: "Which Linear team belongs to this project?",
      },
      allTickets: "All tickets",
      allTicketsOf: "All tickets ({account})",
      allTicketsHint: "Every ticket from this Freshdesk that is linked to a FixControl issue belongs to this project.",
      empty: "Nothing was found in {system} to choose from.",
      odooLabel: "Helpdesk team number",
      odooHelp: "In Odoo, open Helpdesk, then the team. The number is at the end of the address (…/helpdesk.team/3 means 3).",
      odooTeamName: "Helpdesk team {id}",
    },
    purposes: {
      question: "What should FixControl do with these tickets in {project}?",
      hint: {
        intake: "A ticket that is linked to a FixControl issue counts as work for this project, and its status follows along. A new ticket does not create an issue by itself: create the issue in FixControl and link the ticket.",
        intakeJira: "A ticket that is linked to a FixControl issue counts as work for this project, and its status follows along. New Jira tickets only become issues here when you switch on importing below.",
        notifications: "When work moves forward, FixControl adds a short internal note to the ticket in {system}.",
      },
      pickOne: "Choose at least one.",
      readsTitle: "What FixControl reads",
      reads: "Linked tickets and their status in {system}, and earlier tickets when it looks up related history.",
      writesTitle: "What FixControl writes",
      writes: "Internal progress notes on the ticket in {system}. A reply to your customer is only sent after someone approves it.",
      writesNone: "Nothing. FixControl only reads from {system}.",
      connect: "Connect to {project}",
    },
    done: {
      title: "{queue} now brings work into {project}.",
      body: "New tickets appear under this project's issues. You can change or remove this on the Connections page.",
      moreSync: "More sync options (company-wide)",
    },
    /** Jira only: the per-binding opt-in that turns new Jira tickets into issues. */
    jiraImport: {
      label: "Import new Jira tickets as issues",
      needsWebhook: "This only works once the Jira webhook is set up.",
      webhookLink: "Set up the webhook",
      dialogNote: "FixControl posts progress and status back to Jira. New Jira tickets only become issues here when importing is switched on.",
      doneOn: "New Jira tickets become issues in {project} as soon as the Jira webhook is set up. An administrator does that on the Jira page.",
      doneOff: "New Jira tickets do not become issues here yet. You can switch that on later under Manage on the Connections page.",
    },
  },

  projectSetupChat: {
    title: "Add a chat channel",
    noPermission: "Only an administrator of this project or of your organization can connect chat channels to this project.",
    checkAgain: "Check again",
    steps: {
      app: "Which chat app?",
      connect: "Connect",
      channels: "Choose channels",
      purposes: "What happens there?",
      test: "Test",
      done: "Done",
    },
    apps: {
      slack: { name: "Slack", hint: "Channels in your Slack workspace" },
      teams: { name: "Microsoft Teams", hint: "Channels in your Teams" },
    },
    app: {
      intro: "Where does your team talk? FixControl can take new requests from a channel, post progress there and ask for approvals.",
    },
    slack: {
      explain: "Your organization connects its Slack workspace once. Each project then chooses its own channels.",
      useExisting: "Use your organization's Slack workspace",
      existingHint: "Already connected for the whole organization.",
      signInExplain: "You sign in with Slack and add the FixControl app to your workspace. You come back here afterwards.",
      signIn: "Continue to Slack",
      returned: "Slack is connected. Choose the channels.",
      onlyAdmin: "Your organization has not connected Slack yet, and only an administrator of your organization can.",
      notConfigured: "Signing in with Slack is not set up on this FixControl installation. Ask your FixControl administrator.",
      advanced: "Advanced / own Slack app",
      advancedBody: "Only if your organization uses its own Slack app with its own tokens. This is set up on the company Slack page.",
      advancedLink: "Open the company Slack page",
    },
    teams: {
      explain:
        "Microsoft Teams uses one Teams connection that an administrator sets up for the whole organization. Once it is there, you choose this project's channels here.",
      ready: "Your organization's Teams connection is ready.",
      notConnected: "Your organization has no Teams connection yet. Set it up once for the whole organization, then come back here.",
      notConnectedAskAdmin: "Your organization has no Teams connection yet. Ask an administrator of your organization to set it up, then come back here.",
      openSetup: "Set up the Teams connection",
      noProgressUpdates: "Teams does not receive progress updates yet.",
    },
    channels: {
      question: "Which channels should {project} use?",
      search: "Search channels",
      empty: {
        slack: "No channels found. Invite the FixControl app to a channel in Slack, then check again.",
        teams: "No channels found yet. Add the FixControl app to the team and mention @FixControl in the channel once, then check again.",
      },
    },
    purposes: {
      intro: "Decide per channel what happens there. You can change this later.",
      hint: {
        intake: "Messages posted in this channel can become new work in this project.",
        notifications: "FixControl posts here when work on this project moves forward.",
        approvals: "People here are asked to approve or reject actions that need a human decision.",
      },
      pickOne: "Choose at least one use for a channel.",
      save: "Save for {project}",
    },
    test: {
      saved: "The channels are connected to {project}.",
      explain: "Want to be sure? FixControl can post one visible test message. This is optional.",
      send: "Send a test message to {channel}",
      sending: "Sending the test message…",
      skip: "Skip",
      unavailable: "A test message is not available for this channel.",
    },
    done: {
      title: "{channels} now work for {project}.",
    },
  },
};

export const connectionsSetupNl: typeof connectionsSetupEn = {
  projectConnections: {
    title: "Verbindingen",
    subtitle: "Welke systemen dit project gebruikt, of ze werken, en er een toevoegen.",
    scopeNote:
      "Verbindingen horen bij je organisatie en kunnen door meerdere projecten worden gebruikt. Hier kies je welke dit project gebruikt, en waarvoor. Als je er een uit dit project haalt, blijft hij voor de rest van het bedrijf gewoon verbonden.",
    readOnly: "Je kunt de verbindingen van dit project bekijken. Vraag een beheerder van dit project of van je organisatie om er een toe te voegen of te wijzigen.",
    orgAdmins: "Beheerders van je organisatie: {names}.",
    canUseNotCreate:
      "Je kunt dit project koppelen aan verbindingen die je organisatie al heeft. Voor een nieuwe bedrijfsverbinding is een beheerder van je organisatie nodig.",
    askAdmin: "Vraag een projectbeheerder",
    loadError: "De verbindingen van dit project konden niet worden geladen. Laad de pagina opnieuw om het nog eens te proberen.",
    groups: {
      email: {
        title: "E-mail",
        description: "Mailboxen waarvan de mail werk wordt in dit project.",
        add: "Mailbox koppelen",
        empty: "Er is nog geen mailbox aan dit project gekoppeld.",
        emptyAction: "Mailbox koppelen",
      },
      chat: {
        title: "Chat",
        description: "Slack- of Microsoft Teams-kanalen voor nieuwe verzoeken, voortgang en goedkeuringen.",
        add: "Chatkanaal toevoegen",
        empty: "Er is nog geen chatkanaal aan dit project gekoppeld.",
        emptyAction: "Chatkanaal toevoegen",
      },
      support: {
        title: "Supportsoftware",
        description: "Je helpdesk of issuetracker: Freshdesk, TOPdesk, Odoo, Jira of Linear.",
        add: "Supportsoftware koppelen",
        empty: "Er is nog geen supportsoftware aan dit project gekoppeld.",
        emptyAction: "Supportsoftware koppelen",
      },
      code: {
        title: "Code",
        description: "De repository's die FixControl leest en waarvoor het wijzigingen voorstelt.",
        add: "Repository koppelen",
        empty: "Er is nog geen repository aan dit project gekoppeld.",
        emptyAction: "Repository koppelen",
      },
      deployments: {
        title: "Deployments en meldingen",
        technical: "Voor technische teams",
        show: "Deployments en meldingen (voor technische teams)",
        description: "Kubernetes-clusters, Argo-rollouts en systeemmeldingen die bij dit project horen.",
        add: "Deploymentverbinding toevoegen",
        empty: "Nog geen deploymentverbinding. De meeste projecten hebben er geen nodig.",
        emptyAction: "Deploymentverbinding toevoegen",
      },
    },
    status: {
      working: "Werkt",
      untested: "Nog niet getest",
      attention: "Aandacht nodig",
      reconnect: "Opnieuw verbinden",
      disabled: "Uitgeschakeld",
    },
    statusHint: {
      working: "Alles is in orde bevonden.",
      untested: "Ingesteld, maar er is nog niet bewezen dat het werkt. Test de verbinding om het zeker te weten.",
      attention: "Er is iets mis met deze verbinding. Test hem om te zien wat.",
      reconnect: "De aanmelding bij {provider} is verlopen of ingetrokken. Meld je opnieuw aan om hem te herstellen.",
      disabled: "Gepauzeerd voor dit project. Er wordt niets via ontvangen of verstuurd.",
      noCompanyConnection: "Niemand heeft {provider} al voor je organisatie verbonden, dus dit kan nog niet werken.",
      deliveryFailing: "Recente berichten naar deze verbinding konden niet worden afgeleverd.",
    },
    card: {
      unnamed: "Naamloze {provider}-verbinding",
      usedFor: "Gebruikt door dit project voor: {purposes}",
      companyConnection: "Bedrijfsverbinding: {account}",
      companyConnectionGeneric: "Bedrijfsverbinding: {provider} van je organisatie",
      lastChecked: "Laatst gecontroleerd",
      neverChecked: "Nog niet gecontroleerd",
      test: "Verbinding testen",
      testing: "Bezig met testen…",
      manage: "Beheren",
      remove: "Uit dit project halen",
      reconnect: "Opnieuw verbinden",
      fixConnection: "Verbinding herstellen",
      defaultRepository: "Standaardrepository",
      codeSourceOther: "Server of map",
    },
    purpose: {
      intake: "Nieuwe verzoeken worden hier werk",
      approvals: "Goedkeuringsverzoeken",
      notifications: "Voortgang",
      status: "Status terug naar de bron",
      delivery: "Voorgestelde wijzigingen (pull requests)",
      deployment: "Deployments",
      incidents: "Meldingen worden hier werk",
      code: "Code waar FixControl aan werkt",
    },
    purposeSupport: {
      intake: "Gekoppelde tickets horen bij dit project",
      notifications: "Voortgang terugzetten op het ticket",
      status: "Ticketstatus volgt het werk",
    },
    purposeChat: {
      intake: "Nieuwe verzoeken uit dit kanaal",
      notifications: "Voortgang",
      approvals: "Goedkeuringsverzoeken",
    },
    checkMessage: {
      provider_health_pass: "De verbinding met {provider} werkt.",
      provider_health_unknown: "{provider} is de laatste tijd niet gecontroleerd. De uitkomst is nog niet zeker.",
      provider_health_degraded: "{provider} werkt, maar niet alles ging de laatste tijd goed.",
      provider_health_disabled: "Deze verbinding staat uit.",
      provider_health_no_connection_needed: "Voor deze verbinding is geen aanmelding nodig.",
      test_message_pass: "Het testbericht is geplaatst.",
      test_message_queued: "Het testbericht staat klaar en wordt zo geplaatst.",
      test_message_confirmation_required: "Er is geen testbericht verstuurd, omdat daar jouw bevestiging voor nodig is.",
      test_message_organization_only: "Er is geen testbericht verstuurd. Alleen een beheerder van je organisatie kan er een in een kanaal plaatsen.",
    },
    checkStatus: {
      pass: "Geslaagd.",
      fail: "Niet geslaagd.",
      warn: "Geslaagd, met een waarschuwing.",
      skipped: "Niet gecontroleerd.",
    },
    test: {
      confirmTitle: "Testbericht sturen naar {name}?",
      confirmBody:
        "FixControl plaatst één kort, zichtbaar testbericht in {name}. Iedereen in het kanaal kan het zien. Verder verandert er niets.",
      confirmAction: "Testbericht sturen",
    },
    remove: {
      title: "{name} uit dit project halen?",
      body:
        "Dit project gebruikt {name} dan niet meer. De bedrijfsverbinding met {provider} blijft bestaan, en andere projecten die hem gebruiken merken er niets van. Je kunt hem later weer toevoegen.",
      bodyCodebase:
        "Dit project werkt dan niet meer aan {name}. De repository blijft verbonden met je organisatie, en andere projecten die hem gebruiken merken er niets van.",
      confirm: "Uit dit project halen",
      failed: "Het lukte niet om hem weg te halen. Probeer het opnieuw, of vraag een projectbeheerder.",
    },
    manage: {
      loadFailedTitle: "Deze verbinding kon niet worden geopend",
      loadFailed: "De gegevens van deze verbinding konden niet worden geladen.",
    },
    twoFactor: {
      setup: "Stel voor je veiligheid eerst tweestapsverificatie in voor je account. Probeer het daarna opnieuw.",
      reauth: "Meld je opnieuw aan om je tweestapsverificatie te bevestigen, en probeer het daarna opnieuw.",
    },
    globalPages: {
      infraGitHint: "GitHub, GitLab en Bitbucket verbind je niet hier, maar onder Channels → Code- & patch-levering.",
      infraGitLink: "GitHub, GitLab of Bitbucket verbinden",
      codebasesGitCta: "GitHub, GitLab of Bitbucket verbinden",
      codebasesServerCta: "Staat je code op een eigen server of in een lokale map? Verbind die dan hier.",
    },
    wizard: {
      stepOf: "Stap {step} van {total}",
      finished: "Afgerond",
      dialogLabel: "{wizard} voor {project}",
      back: "Terug",
      next: "Volgende",
      cancel: "Annuleren",
      close: "Sluiten",
      done: "Klaar",
      tryAgain: "Opnieuw proberen",
      loading: "Laden…",
      working: "Bezig…",
      technicalDetails: "Technische details",
    },
  },

  projectSetupRepo: {
    title: "Repository koppelen",
    noPermission: "Alleen een beheerder van je organisatie kan een repository aan dit project koppelen.",
    onlyExisting:
      "Je kunt een repository koppelen die je organisatie al heeft. Alleen een beheerder van je organisatie kan een nieuwe toevoegen.",
    genericProvider: "de codehost",
    linkButton: "Koppelen aan {project}",
    retryLink: "Opnieuw koppelen",
    partial:
      "De repository is aan je organisatie toegevoegd, maar het koppelen aan {project} lukte niet. Probeer het opnieuw; er wordt niets dubbel toegevoegd.",
    steps: {
      provider: "Waar staat de code?",
      connect: "Aanmelden",
      organization: "Organisatie kiezen",
      repository: "Repository kiezen",
      folder: "Map kiezen",
      branch: "Branch kiezen",
      check: "Controle",
      done: "Klaar",
    },
    sources: {
      github: { name: "GitHub", hint: "" },
      gitlab: { name: "GitLab", hint: "" },
      bitbucket: { name: "Bitbucket", hint: "" },
      local: { name: "Map op een computer", hint: "Een map op een computer waarop de FixControl-desktopapp draait." },
      ssh: { name: "Server (SSH)", hint: "Een map op een van je eigen servers, bereikbaar via SSH." },
    },
    provider: {
      question: "Waar staat de code?",
      intro: "Kies waar de code van dit project staat. FixControl leest hem en stelt wijzigingen voor als pull request; het past je hoofdbranch nooit rechtstreeks aan.",
      more: "Meer opties (technisch)",
      moreHint: "Alleen als de code niet op GitHub, GitLab of Bitbucket staat.",
    },
    existing: {
      title: "Al verbonden in je organisatie",
      hint: "Een ander project gebruikt deze al. Kies er een om hem hier ook te gebruiken.",
    },
    connect: {
      intro: "FixControl bereikt {provider} via een verbinding die bij je organisatie hoort. Dit project gebruikt daar dan één repository van.",
      introServer: "FixControl bereikt deze code via een verbinding die bij je organisatie hoort.",
      useExisting: "Een verbinding gebruiken die je organisatie al heeft",
      companyConnection: "Bedrijfsverbinding: {account}",
      none: "Je organisatie heeft nog geen {provider}-verbinding.",
      noneVisible: "Er is geen {provider}-verbinding voor jou zichtbaar.",
      newTitle: "Nieuw {provider}-account verbinden",
      explain:
        "Je meldt je aan bij {provider} en kiest welke repository's FixControl mag lezen. Daarna kom je hier terug. De verbinding hoort bij je organisatie; je kunt hem later weer verwijderen.",
      signIn: "Verder naar {provider}",
      returned: "{provider} is verbonden. Kies de repository.",
      onlyAdmin: "Alleen een beheerder van je organisatie kan een nieuw {provider}-account verbinden. Vraag het hun, of gebruik een bestaande verbinding.",
      notConfigured: "Aanmelden met {provider} is niet ingesteld op deze FixControl-installatie. Vraag je FixControl-beheerder.",
      useToken: "In plaats daarvan verbinden met een toegangstoken (bedrijfspagina)",
      addServer: "Een server- of mapverbinding toevoegen (bedrijfspagina)",
    },
    organization: {
      intro: "Deze {provider}-verbinding ziet meerdere organisaties. In welke staat de code?",
      count: "{n} repository's",
    },
    repository: {
      loading: "Repository's laden…",
      search: "Repository's zoeken",
      empty: "Deze {provider}-verbinding ziet nog geen repository's. Geef FixControl in {provider} toegang tot de repository en kom dan terug.",
      noMatches: "Geen repository gevonden die past bij je zoekopdracht.",
      pickConnectionFirst: "Kies eerst een verbinding.",
      manualLabel: "Repository",
      manualHint: "Typ hem als organisatie/naam, zoals hij in {provider} staat.",
      localLabel: "Map",
      sshLabel: "Map op de server",
      pathHint: "Het volledige pad van de map met de code.",
    },
    branch: {
      label: "Branch",
      hint: "FixControl stelt wijzigingen voor op deze branch van {repo}. Het pusht er nooit rechtstreeks naartoe: wijzigingen komen binnen als pull request, zodat je team ze kan beoordelen.",
    },
    check: {
      linked: "{repo} is gekoppeld aan {project}.",
      read: {
        running: "Controleren of FixControl de repository kan lezen…",
        ok: "FixControl kan de repository lezen.",
        fail: "FixControl kon de repository niet lezen. Controleer of de verbinding er toegang toe heeft.",
        skipped: "Het lezen van de repository is niet gecontroleerd. Een beheerder kan het testen op de pagina Verbindingen.",
      },
      sandbox: {
        ready: "Testomgeving: FixControl kan voorgestelde wijzigingen uitproberen in een veilige kopie.",
        notYet: "Testomgeving: nog niet gecontroleerd. Je kunt hem bekijken en testen op de pagina Omgevingen.",
      },
      preview: {
        ready: "Preview: beschikbaar. Je kunt een voorgestelde wijziging in je browser openen voordat je hem goedkeurt.",
        notYet: "Preview: komt beschikbaar zodra FixControl heeft vastgesteld dat de applicatie start.",
      },
      openEnvironments: "Omgevingen openen",
    },
    done: {
      title: "{repo} is nu gekoppeld aan {project}.",
      body: "FixControl leest deze code als het aan de issues van dit project werkt, en stelt wijzigingen voor als pull request.",
    },
  },

  projectSetupSupport: {
    title: "Supportsoftware koppelen",
    noPermission: "Alleen een beheerder van dit project of van je organisatie kan supportsoftware aan dit project koppelen.",
    steps: {
      system: "Welk systeem?",
      connect: "Verbinden",
      test: "Testen",
      pick: "Welke tickets?",
      purposes: "Wat moet er gebeuren?",
      done: "Klaar",
    },
    systems: {
      freshdesk: { name: "Freshdesk", hint: "Helpdesktickets" },
      topdesk: { name: "TOPdesk", hint: "Servicedeskmeldingen" },
      odoo: { name: "Odoo Helpdesk", hint: "Helpdesktickets in Odoo" },
      jira: { name: "Jira", hint: "Issues in Jira-projecten" },
      linear: { name: "Linear", hint: "Issues in Linear-teams" },
    },
    system: {
      intro: "In welk systeem melden je klanten of collega's problemen? FixControl maakt van tickets daaruit werk in dit project.",
    },
    connect: {
      intro: "FixControl bereikt {system} via een verbinding die bij je organisatie hoort. Dit project gebruikt daar dan een deel van.",
      useExisting: "De verbinding gebruiken die je organisatie al heeft",
      new: "Nieuw {system}-account verbinden",
      newHint: "Eén keer, voor de hele organisatie. Andere projecten kunnen hem ook gebruiken.",
      onlyAdmin: "Je organisatie heeft nog geen {system}-verbinding, en alleen een beheerder van je organisatie kan er een toevoegen.",
      formIntro: "Vul deze gegevens uit {system} in. Ze worden versleuteld opgeslagen en nooit meer getoond.",
      companyWide: "Hiermee verbind je {system} voor je hele organisatie. Wat dit project gebruikt, kies je in de volgende stappen.",
      save: "{system} verbinden",
      oauthExplain: "Je meldt je aan bij {system} en geeft FixControl toestemming om issues te lezen en bij te werken. Daarna kom je hier terug.",
      signIn: "Verder naar {system}",
      notConfigured: "Aanmelden met {system} is niet ingesteld op deze FixControl-installatie.",
      useToken: "In plaats daarvan verbinden met een API-token (bedrijfspagina)",
    },
    fields: {
      freshdesk: {
        baseUrl: { label: "Je Freshdesk-adres", placeholder: "https://acme.freshdesk.com", help: "Het adres waarop je Freshdesk opent, bijvoorbeeld acme.freshdesk.com." },
        token: { label: "Freshdesk-API-sleutel", placeholder: "Plak de API-sleutel", help: "In Freshdesk: je profielfoto, dan Profielinstellingen. De API-sleutel staat rechts." },
      },
      topdesk: {
        baseUrl: { label: "Je TOPdesk-adres", placeholder: "https://acme.topdesk.net", help: "Het adres waarop je TOPdesk opent, bijvoorbeeld acme.topdesk.net." },
        username: { label: "Inlognaam behandelaar", placeholder: "fixcontrol", help: "De inlognaam van de TOPdesk-behandelaar namens wie FixControl werkt." },
        token: { label: "Applicatiewachtwoord", placeholder: "Plak het applicatiewachtwoord", help: "In TOPdesk: de kaart van de behandelaar, dan Applicatiewachtwoorden. Niet het gewone wachtwoord." },
      },
      odoo: {
        baseUrl: { label: "Je Odoo-adres", placeholder: "https://acme.odoo.com", help: "Het adres waarop je Odoo opent, bijvoorbeeld acme.odoo.com." },
        host: { label: "Databasenaam", placeholder: "acme", help: "Vaak het eerste deel van je Odoo-adres." },
        username: { label: "Inlog-e-mailadres", placeholder: "jij@acme.com", help: "Het e-mailadres van de Odoo-gebruiker namens wie FixControl werkt." },
        token: { label: "API-sleutel", placeholder: "Plak de API-sleutel", help: "In Odoo: Voorkeuren, dan Accountbeveiliging, dan Nieuwe API-sleutel." },
      },
    },
    test: {
      created: "{system} is verbonden voor je organisatie.",
      running: "De verbinding met {system} controleren…",
      ok: "FixControl kan {system} bereiken.",
      skipped: "De verbinding is niet getest, omdat alleen een beheerder dat kan. Hij wordt gebruikt zoals hij is.",
      continueAnyway: "Je kunt doorgaan en dit later oplossen; het project toont deze verbinding dan als 'Aandacht nodig'.",
    },
    pick: {
      question: {
        freshdesk: "Welke tickets horen bij dit project?",
        topdesk: "Van welke behandelaarsgroep horen de meldingen bij dit project?",
        odoo: "Van welk helpdeskteam horen de tickets bij dit project?",
        jira: "Welk Jira-project hoort bij dit project?",
        linear: "Welk Linear-team hoort bij dit project?",
      },
      allTickets: "Alle tickets",
      allTicketsOf: "Alle tickets ({account})",
      allTicketsHint: "Elk ticket uit deze Freshdesk dat aan een FixControl-issue is gekoppeld, hoort bij dit project.",
      empty: "Er is in {system} niets gevonden om uit te kiezen.",
      odooLabel: "Nummer van het helpdeskteam",
      odooHelp: "Open in Odoo Helpdesk en dan het team. Het nummer staat aan het eind van het adres (…/helpdesk.team/3 betekent 3).",
      odooTeamName: "Helpdeskteam {id}",
    },
    purposes: {
      question: "Wat moet FixControl met deze tickets doen in {project}?",
      hint: {
        intake: "Een ticket dat aan een FixControl-issue is gekoppeld, telt als werk voor dit project en de status loopt mee. Een nieuw ticket maakt niet vanzelf een issue: maak het issue in FixControl en koppel het ticket.",
        intakeJira: "Een ticket dat aan een FixControl-issue is gekoppeld, telt als werk voor dit project en de status loopt mee. Nieuwe Jira-tickets worden hier alleen een issue als je het binnenhalen hieronder aanzet.",
        notifications: "Als het werk vordert, zet FixControl een korte interne notitie op het ticket in {system}.",
      },
      pickOne: "Kies er minstens één.",
      readsTitle: "Wat FixControl leest",
      reads: "Gekoppelde tickets en hun status in {system}, en eerdere tickets wanneer het verwante historie opzoekt.",
      writesTitle: "Wat FixControl schrijft",
      writes: "Interne voortgangsnotities op het ticket in {system}. Een antwoord aan je klant gaat pas de deur uit nadat iemand het heeft goedgekeurd.",
      writesNone: "Niets. FixControl leest alleen uit {system}.",
      connect: "Koppelen aan {project}",
    },
    done: {
      title: "{queue} brengt nu werk binnen in {project}.",
      body: "Nieuwe tickets verschijnen bij de issues van dit project. Je kunt dit wijzigen of weghalen op de pagina Verbindingen.",
      moreSync: "Meer synchronisatie-opties (hele bedrijf)",
    },
    jiraImport: {
      label: "Nieuwe Jira-tickets als issue binnenhalen",
      needsWebhook: "Dit werkt pas als de Jira-webhook is ingesteld.",
      webhookLink: "Webhook instellen",
      dialogNote: "FixControl zet voortgang en status terug in Jira. Nieuwe Jira-tickets worden hier alleen een issue als binnenhalen aan staat.",
      doneOn: "Nieuwe Jira-tickets worden een issue in {project} zodra de Jira-webhook is ingesteld. Een beheerder doet dat op de Jira-pagina.",
      doneOff: "Nieuwe Jira-tickets worden hier nog geen issue. Je kunt dat later aanzetten via Beheren op de pagina Verbindingen.",
    },
  },

  projectSetupChat: {
    title: "Chatkanaal toevoegen",
    noPermission: "Alleen een beheerder van dit project of van je organisatie kan chatkanalen aan dit project koppelen.",
    checkAgain: "Opnieuw controleren",
    steps: {
      app: "Welke chatapp?",
      connect: "Verbinden",
      channels: "Kanalen kiezen",
      purposes: "Wat gebeurt daar?",
      test: "Testen",
      done: "Klaar",
    },
    apps: {
      slack: { name: "Slack", hint: "Kanalen in je Slack-werkruimte" },
      teams: { name: "Microsoft Teams", hint: "Kanalen in je Teams" },
    },
    app: {
      intro: "Waar praat je team? FixControl kan nieuwe verzoeken uit een kanaal halen, er voortgang plaatsen en om goedkeuring vragen.",
    },
    slack: {
      explain: "Je organisatie verbindt haar Slack-werkruimte één keer. Elk project kiest daarna zijn eigen kanalen.",
      useExisting: "De Slack-werkruimte van je organisatie gebruiken",
      existingHint: "Al verbonden voor de hele organisatie.",
      signInExplain: "Je meldt je aan bij Slack en voegt de FixControl-app toe aan je werkruimte. Daarna kom je hier terug.",
      signIn: "Verder naar Slack",
      returned: "Slack is verbonden. Kies de kanalen.",
      onlyAdmin: "Je organisatie heeft Slack nog niet verbonden, en alleen een beheerder van je organisatie kan dat doen.",
      notConfigured: "Aanmelden met Slack is niet ingesteld op deze FixControl-installatie. Vraag je FixControl-beheerder.",
      advanced: "Geavanceerd / eigen Slack-app",
      advancedBody: "Alleen als je organisatie een eigen Slack-app met eigen tokens gebruikt. Dat stel je in op de Slack-pagina van het bedrijf.",
      advancedLink: "Slack-pagina van het bedrijf openen",
    },
    teams: {
      explain:
        "Microsoft Teams gebruikt één Teams-verbinding die een beheerder voor de hele organisatie instelt. Zodra die er is, kies je hier de kanalen van dit project.",
      ready: "De Teams-verbinding van je organisatie is klaar.",
      notConnected: "Je organisatie heeft nog geen Teams-verbinding. Stel die één keer in voor de hele organisatie en kom dan hier terug.",
      notConnectedAskAdmin: "Je organisatie heeft nog geen Teams-verbinding. Vraag een beheerder van je organisatie om die in te stellen en kom dan hier terug.",
      openSetup: "Teams-verbinding instellen",
      noProgressUpdates: "Teams ontvangt nog geen voortgangsberichten.",
    },
    channels: {
      question: "Welke kanalen moet {project} gebruiken?",
      search: "Kanalen zoeken",
      empty: {
        slack: "Geen kanalen gevonden. Nodig de FixControl-app uit in een kanaal in Slack en controleer het dan opnieuw.",
        teams: "Nog geen kanalen gevonden. Voeg de FixControl-app toe aan het team en noem @FixControl één keer in het kanaal. Controleer het daarna opnieuw.",
      },
    },
    purposes: {
      intro: "Bepaal per kanaal wat er gebeurt. Je kunt dit later nog wijzigen.",
      hint: {
        intake: "Berichten in dit kanaal kunnen nieuw werk worden in dit project.",
        notifications: "FixControl plaatst hier een bericht als het werk aan dit project vordert.",
        approvals: "Mensen hier krijgen de vraag om acties die een menselijke beslissing nodig hebben goed of af te keuren.",
      },
      pickOne: "Kies voor een kanaal minstens één gebruik.",
      save: "Opslaan voor {project}",
    },
    test: {
      saved: "De kanalen zijn gekoppeld aan {project}.",
      explain: "Wil je het zeker weten? FixControl kan één zichtbaar testbericht plaatsen. Dat is optioneel.",
      send: "Testbericht sturen naar {channel}",
      sending: "Testbericht versturen…",
      skip: "Overslaan",
      unavailable: "Voor dit kanaal is geen testbericht beschikbaar.",
    },
    done: {
      title: "{channels} werken nu voor {project}.",
    },
  },
};
